/**
 * Where a fitted part comes off (piece 29, P29-16; decision 10320, amending
 * 0530's fixed shelf): the technician's van first, the job's shop second, and
 * Another Site over the organization's own open sites (T3419, Q9a). The Add
 * Part window reads one answer, and the material write names the site chosen.
 */

/** One stock the window offers, with its on-hand of the part in the Item's own unit. */
export interface PartSourceSite {
  siteId: string;
  siteName: string;
  onHand: number;
}

/** The answer the Add Part window reads for one part on one Work Order. */
export interface WorkOrderPartSourcesAnswer {
  /** False for a Service, which is fitted from no shelf and asks nothing. */
  movesStock: boolean;
  /** The site of the recording person's open `VanAssignment` row, or null where none is open. */
  van: PartSourceSite | null;
  /** The job's travel origin, falling back to the dispatch default (decision 0530), or null where neither names one. */
  shop: PartSourceSite | null;
  /** The site of the latest part this person recorded on this Work Order, read from that movement. */
  rememberedSiteId: string | null;
  /** The site the window opens on: the remembered site, else the van, else the shop. */
  firstSiteId: string | null;
  /** The organization's own open sites, each with its on-hand, for Another Site (decision 0451). */
  sites: PartSourceSite[];
}

/** The window's read, gated as the Work Order's own read. */
export function workOrderPartSourcesPath(workOrderId: string, itemId: string): string {
  return `/work-orders/${encodeURIComponent(workOrderId)}/part-sources?itemId=${encodeURIComponent(itemId)}`;
}

/** Settings › Service › Dispatch Rules › Travel Billing's Vans card: the open assignments, and + Assign Van's write. */
export const VAN_ASSIGNMENTS_PATH = '/settings/accounting/van-assignments';

/** 🔒 The site the window opens on (decision 10320): what this person last fitted from here, else the van, else the shop. */
export function partSourceFirst(facts: { rememberedSiteId: string | null; vanSiteId: string | null; shopSiteId: string | null }): string | null {
  return facts.rememberedSiteId ?? facts.vanSiteId ?? facts.shopSiteId ?? null;
}

/** 🔨 A customer's site is a place we visit, never a shelf we count (decision 0451), so a part never comes off one. */
export function partSiteNotOursProblem(siteName: string): string {
  return `The site ${siteName} is a customer's site, not one of ours, so no part can come off it. Choose the van, the shop or another of our own sites.`;
}

/** 🔨 A site of ours that has closed holds no shelf a part can come off. */
export function partSiteClosedProblem(siteName: string): string {
  return `The site ${siteName} has closed, so no part can come off it. Choose the van, the shop or another of our own sites.`;
}

/** One open van assignment as the Vans card lists it. */
export interface VanAssignmentRow {
  id: string;
  employmentId: string;
  technicianName: string;
  vanSiteId: string;
  vanName: string;
  /** The first day, `YYYY-MM-DD`. */
  startDate: string;
  assignedByName: string;
}

/** What the Vans card reads: the open rows, and what + Assign Van offers. */
export interface VanAssignmentsAnswer {
  rows: VanAssignmentRow[];
  /** The open employments at the organization's own company. */
  technicians: { employmentId: string; name: string }[];
  /** The organization's own open sites, the only places a van can be (decision 0451). */
  vans: { siteId: string; name: string }[];
}

/** The body + Assign Van posts. */
export interface VanAssignmentInput {
  employmentId: string;
  vanSiteId: string;
  /** `YYYY-MM-DD`. */
  startDate: string;
}

/**
 * 🔨 The refusals an assignment meets before it writes (decision 10320): every
 * field named, and a new row starting after the open one and naming another van.
 * The open row is closed on the day before the new one starts.
 */
export function vanAssignmentProblem(input: Partial<VanAssignmentInput>, open: { vanSiteId: string; startDate: string } | null): string | null {
  if (!input.employmentId) return 'A van assignment names the technician.';
  if (!input.vanSiteId) return 'A van assignment names the van.';
  if (!input.startDate || !/^\d{4}-\d{2}-\d{2}$/.test(input.startDate)) return 'A van assignment names the day it starts.';
  if (open && open.vanSiteId === input.vanSiteId) return 'This technician is already assigned to that van.';
  if (open && input.startDate <= open.startDate) return `The technician's current van was assigned from ${open.startDate}, so a new one starts after that day.`;
  return null;
}
